import { createPortal } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DoorOpen } from 'lucide-react';

import {
	ButtonGroup,
	DialogIcon,
	DialogTitle,
	PunchupDialogue,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupInput } from 'features/common/components/Punchup/primitives/forms/fields/PunchupInput';

import { AddGateFormData } from '../types';
import styles from '../PunchupGatesManagement.module.css';

interface AddGateDialogProps {
	isCreating: boolean;
	onClose: () => void;
	onSave: (name: string) => void;
}

export const AddGateDialog = ({ isCreating, onClose, onSave }: AddGateDialogProps) => {
	const { t } = useTranslation();
	const methods = useForm<AddGateFormData>({ defaultValues: { name: '' } });

	const submit = (data: AddGateFormData) => {
		const name = data.name.trim();
		if (name) onSave(name);
	};

	return createPortal(
		<PunchupDialogue maxWidth={420} isLoading={isCreating} onClose={onClose}>
			<DialogIcon>
				<DoorOpen size={24} />
			</DialogIcon>
			<DialogTitle>{t('gates-add-title', 'Add a gate')}</DialogTitle>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(submit)} className={styles.dialogForm}>
					<PunchupInput id="gate-name" name="name" label={t('gates-name-label', 'Gate name')} required />
					<ButtonGroup>
						<PunchupButton
							type="button"
							variant="secondary"
							text={t('button-cancel', 'Cancel')}
							onClick={onClose}
							disabled={isCreating}
						/>
						<PunchupButton
							type="submit"
							variant="primary"
							text={t('gates-add-cta', 'Add gate')}
							disabled={isCreating}
						/>
					</ButtonGroup>
				</form>
			</FormProvider>
		</PunchupDialogue>,
		document.body
	);
};
